import React, { createContext, useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { googleLogout, useGoogleLogin } from '@react-oauth/google';
import Cookies from 'universal-cookie';
import { getTokens } from "../services/AuthService";
import { validateEmployee, getEmployeeById } from "../services/EmployeesService";

const AuthContext = createContext();

function AuthProvider({ children }) {
    const cookies = new Cookies();

    const [userId, setUserId] = useState(cookies.get("userId") || null);
    const [token, setToken] = useState(cookies.get("jwt_authorization") || null);
    const [accessToken, setAccessToken] = useState(cookies.get("access_token") || null);
    const [user, setUser] = useState(cookies.get("user") || null);
    const [userPicture, setUserPicture] = useState(cookies.get("user_picture") || null);

    useEffect(() => {
        if (userId) cookies.set("userId", userId);
        if (user) cookies.set("user", user);
        if (token) cookies.set("jwt_authorization", token);
        if (userPicture) cookies.set("user_picture", userPicture);
    }, [userId, user, token, userPicture]);

    const handleLoginSuccess = async (response) => {
        try {
            const tokens = await getTokens({ "code": response.code.toString() });
            const userObject = jwtDecode(tokens.id_token);
            const expiryDate = new Date(userObject.exp * 1000);

            cookies.set("jwt_authorization", tokens.id_token, { expires: expiryDate });
            cookies.set("access_token", tokens.access_token, { expires: expiryDate });
            cookies.set("userId", userObject.sub.toString());
            cookies.set("refresh_token", tokens.refresh_token);
            cookies.set("user_picture", userObject.picture);

            setToken(tokens.id_token);
            setAccessToken(tokens.access_token);
            setUserId(userObject.sub);
            setUserPicture(userObject.picture);

            const params = new URLSearchParams();
            params.append('id_token', tokens.id_token);
            await validateEmployee(params);

            const employee = await getEmployeeById(userObject.sub);
            const userRole = employee.role === 'admin' ? 'admin' : 'user';
            setUser({ email: userObject.email, role: userRole });
            cookies.set("user", { email: userObject.email, role: userRole });
        } catch (error) {
            console.error("Error during login:", error);
        }
    };

    const login = useGoogleLogin({
        flow: 'auth-code',
        scope: 'https://www.googleapis.com/auth/calendar',
        onSuccess: handleLoginSuccess,
        onError: (errorResponse) => console.log(errorResponse),
    });

    const logout = () => {
        googleLogout();
        setUser(null);
        setUserId(null);
        setToken(null);
        setUserPicture(null);
        setAccessToken(null);
        cookies.remove("jwt_authorization");
        cookies.remove("userId");
        cookies.remove("refresh_token");
        cookies.remove("user_picture");
        cookies.remove("user");
        cookies.remove("access_token");
    };

    return (
        <AuthContext.Provider value={{ user, setUser, userId, token, accessToken, userPicture, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export { AuthContext, AuthProvider };
